import { Controller } from "stimulus"

type DetailError = {
  id: number | string
  question_id?: number | string
  errors: string[]
}

type ErrorResponse = {
  success?: boolean
  errors?: string[]
  detail_errors?: DetailError[]
}

export default class extends Controller {
  static targets = ["message", "submit", "detail"]

  static values = {
    successMessage: String,
    errorMessage: String,
    fixItemsOneMessage: String,
    fixItemsOtherMessage: String,
  }

  messageTarget: HTMLElement
  submitTarget: HTMLInputElement
  detailTargets: HTMLElement[]

  successMessageValue: string
  errorMessageValue: string
  fixItemsOneMessageValue: string
  fixItemsOtherMessageValue: string

  async submit(event: Event): Promise<void> {
    event.preventDefault()

    const form = this.element.querySelector("form") as HTMLFormElement | null
    if (!form) return

    this.setMessage("")
    this.clearFieldErrors()
    this.submitTarget.disabled = true

    try {
      const csrfMeta = document.querySelector('meta[name="csrf-token"]')
      const response = await fetch(form.action, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfMeta ? csrfMeta.getAttribute("content") || "" : "",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: new FormData(form),
      })

      if (response.ok) {
        this.setMessage(this.successMessageValue, "text-success")
        return
      }

      const data = (await response.json().catch(() => null)) as ErrorResponse | null
      const detailErrors = (data && data.detail_errors) || []
      this.applyDetailErrors(detailErrors)
      this.setMessage(this.errorSummary(data), "text-danger")
    } catch (error) {
      this.setMessage(this.errorMessageValue, "text-danger")
    } finally {
      this.submitTarget.disabled = false
    }
  }

  private errorSummary(data: ErrorResponse | null): string {
    const detailErrors = (data && data.detail_errors) || []
    const failedCount = detailErrors.filter(
      (d) => d.errors && d.errors.length
    ).length

    if (failedCount > 0) {
      const template =
        failedCount === 1
          ? this.fixItemsOneMessageValue
          : this.fixItemsOtherMessageValue
      return template.replace("%{count}", String(failedCount))
    }
    if (data && data.errors && data.errors.length) return data.errors[0]

    return this.errorMessageValue
  }

  private applyDetailErrors(detailErrors: DetailError[]): void {
    detailErrors.forEach((detailError) => {
      if (!detailError.errors || !detailError.errors.length) return

      const matchKey =
        detailError.question_id != null
          ? String(detailError.question_id)
          : String(detailError.id)
      const detailEl = this.detailTargets.find(
        (el) => el.dataset.detailId === matchKey
      )
      if (!detailEl) return

      detailEl
        .querySelectorAll("input:not([type='hidden']), select, textarea")
        .forEach((input: HTMLInputElement) => {
          if (input.disabled || input.readOnly) return
          input.classList.add("is-invalid")
        })

      const feedback = document.createElement("div")
      feedback.className = "invalid-feedback d-block"
      feedback.dataset.fieldError = "true"
      feedback.textContent = detailError.errors.join(", ")
      detailEl.appendChild(feedback)
    })
  }

  private clearFieldErrors(): void {
    this.detailTargets.forEach((detailEl) => {
      detailEl
        .querySelectorAll(".is-invalid")
        .forEach((el) => el.classList.remove("is-invalid"))
      detailEl
        .querySelectorAll("[data-field-error]")
        .forEach((el) => el.remove())
    })
  }

  private setMessage(text: string, cssClass = ""): void {
    this.messageTarget.textContent = text
    this.messageTarget.classList.remove("text-success", "text-danger")
    if (cssClass) this.messageTarget.classList.add(cssClass)
  }
}
